import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import {
  Box,
  Typography,
  Button,
  Chip,
  Table,
  Sheet,
  CircularProgress,
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  DialogActions,
  Input,
  Select,
  Option,
  Checkbox,
  Stack,
  FormControl,
  FormLabel,
  LinearProgress,
} from '@mui/joy';
import { useState, useMemo, useEffect, useRef } from 'react';
import { useFeedback } from '../feedback/Service';
import { parseGraphQLError } from '../../utils';
import { getProviderQueries } from './providerQueryMap';
import SearchIcon from '@mui/icons-material/Search';

const ATSCandidateImportDialog = ({ open, onClose, atsJobId, cavalryJobId, providerId = 'ashby' }) => {
  const pq = getProviderQueries(providerId);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [nameFilter, setNameFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [batchId, setBatchId] = useState(null);
  const [batchStatus, setBatchStatus] = useState(null);
  const pollingRef = useRef(null);
  const feedback = useFeedback();

  const { data: candidatesData, loading, error, refetch } = useQuery(pq.LIST_CANDIDATES, {
    variables: { jobId: atsJobId },
    skip: !atsJobId || !open,
    onError: (error) => {
      console.error('Error fetching ATS candidates:', error);
    }
  });

  const { data: importsData, loading: loadingImports, refetch: refetchImports } = useQuery(pq.LIST_IMPORTS, {
    variables: { cavalryJobId },
    skip: !cavalryJobId || !open,
    onError: (error) => {
      console.error('Error fetching imported candidates:', error);
    }
  });

  const [checkBatchStatus] = useLazyQuery(pq.GET_BATCH_STATUS, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const status = data.getAtsBatchStatus;
      setBatchStatus(status);

      if (status.status === 'processing') {
        // Continue polling
        pollingRef.current = setTimeout(() => {
          checkBatchStatus({ variables: { batchId } });
        }, 2000);
      } else {
        // Import finished
        if (status.status === 'completed') {
          feedback.alert({
            title: 'Import Completed',
            text: `${status.completed} candidate${status.completed !== 1 ? 's' : ''} imported successfully! They will receive notification emails with sign-in instructions shortly.`,
          });
        } else if (status.status === 'completed_with_errors') {
          feedback.snackbar({ 
            text: `${status.completed} imported, ${status.failed} failed. ${status.errors?.[0] || ''}`, 
            type: 'warning' 
          });
        }
        // Reset and close
        refetchImports();
        setSelectedCandidates([]);
        setBatchId(null);
        setBatchStatus(null);
        onClose();
      }
    },
    onError: (error) => {
      feedback.snackbar({ text: parseGraphQLError(error), type: 'error' });
      setBatchId(null);
      setBatchStatus(null);
    }
  });

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
      }
    };
  }, []);

  const [bulkImport, { loading: importing }] = useMutation(pq.BULK_IMPORT_CANDIDATES, {
    onCompleted: (data) => {
      const newBatchId = data[pq.bulkImportField];
      setBatchId(newBatchId);
      setBatchStatus({ status: 'processing', total: selectedCandidates.length, completed: 0, failed: 0 });
      // Start polling
      checkBatchStatus({ variables: { batchId: newBatchId } });
    },
    onError: (error) => {
      feedback.snackbar({ text: parseGraphQLError(error), type: 'error' });
    }
  });

  // Get set of already imported candidate IDs
  const importedCandidateIds = useMemo(() => {
    const imports = importsData?.listAtsImports || [];
    return new Set(imports.map(i => i.atsCandidateId));
  }, [importsData]);

  // Filter out already imported candidates
  const candidates = useMemo(() => {
    const allCandidates = candidatesData?.[pq.candidatesField] || [];
    return allCandidates.filter(c => !importedCandidateIds.has(c.id));
  }, [candidatesData, importedCandidateIds, pq.candidatesField]);

  // Get unique stages for filter dropdown
  const stages = useMemo(() => {
    const uniqueStages = [...new Set(candidates.map(c => c.currentStage))];
    return uniqueStages.filter(Boolean).sort();
  }, [candidates]);

  // Filter candidates
  const filteredCandidates = useMemo(() => {
    return candidates.filter(candidate => {
      const matchesName = !nameFilter || candidate.name?.toLowerCase().includes(nameFilter.toLowerCase());
      const matchesEmail = !emailFilter || candidate.email?.toLowerCase().includes(emailFilter.toLowerCase());
      const matchesStage = !stageFilter || candidate.currentStage === stageFilter;
      return matchesName && matchesEmail && matchesStage;
    });
  }, [candidates, nameFilter, emailFilter, stageFilter]);

  const handleSelectAll = () => {
    if (selectedCandidates.length === filteredCandidates.length) {
      setSelectedCandidates([]);
    } else {
      setSelectedCandidates(filteredCandidates.map(c => c.id));
    }
  };

  const handleToggleCandidate = (candidateId) => {
    setSelectedCandidates(prev =>
      prev.includes(candidateId)
        ? prev.filter(id => id !== candidateId)
        : [...prev, candidateId]
    );
  };

  const handleImport = () => {
    const candidatesToImport = candidates
      .filter(c => selectedCandidates.includes(c.id))
      .map(c => ({
        [pq.candidateIdKey]: c.id,
        [pq.applicationIdKey]: c.applicationId,
      }));

    bulkImport({
      variables: {
        candidates: candidatesToImport,
        cavalryJobId,
      }
    });
  };

  const handleClose = () => {
    // Don't allow closing while import is in progress
    if (batchStatus?.status === 'processing') {
      return;
    }
    // Cleanup polling
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
    }
    setSelectedCandidates([]);
    setNameFilter('');
    setEmailFilter('');
    setStageFilter('');
    setBatchId(null);
    setBatchStatus(null);
    onClose();
  };

  const allFilteredSelected = filteredCandidates.length > 0 && selectedCandidates.length === filteredCandidates.length;
  const someSelected = selectedCandidates.length > 0 && selectedCandidates.length < filteredCandidates.length;

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog sx={{ maxWidth: 800, width: '100%', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <ModalClose />
        <DialogTitle>Import Candidates from ATS</DialogTitle>
        <DialogContent sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Filters */}
          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
            <FormControl size="sm" sx={{ minWidth: 150 }}>
              <FormLabel>Name</FormLabel>
              <Input
                placeholder="Filter by name..."
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                startDecorator={<SearchIcon />}
              />
            </FormControl>
            <FormControl size="sm" sx={{ minWidth: 150 }}>
              <FormLabel>Email</FormLabel>
              <Input
                placeholder="Filter by email..."
                value={emailFilter}
                onChange={(e) => setEmailFilter(e.target.value)}
                startDecorator={<SearchIcon />}
              />
            </FormControl>
            <FormControl size="sm" sx={{ minWidth: 150 }}>
              <FormLabel>Stage</FormLabel>
              <Select
                placeholder="All stages"
                value={stageFilter}
                onChange={(_, value) => setStageFilter(value || '')}
              >
                <Option value="">All stages</Option>
                {stages.map(stage => (
                  <Option key={stage} value={stage}>{stage}</Option>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {/* Selection controls */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              size="sm"
              variant="plain"
              onClick={handleSelectAll}
            >
              {allFilteredSelected ? 'Deselect All' : 'Select All'}
            </Button>
            <Typography level="body-sm" color="neutral">
              {selectedCandidates.length} of {filteredCandidates.length} selected
            </Typography>
          </Box>

          {/* Candidates table */}
          <Box sx={{ overflow: 'auto', flex: 1 }}>
            {loading || loadingImports ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, py: 4 }}>
                <CircularProgress size="sm" />
                <Typography level="body-sm">Loading candidates...</Typography>
              </Box>
            ) : error ? (
              <Box sx={{ p: 2, bgcolor: 'warning.softBg', borderRadius: 'sm' }}>
                <Typography level="body-sm" color="warning">
                  Failed to load candidates. Please try again.
                </Typography>
                <Button size="sm" variant="soft" color="warning" sx={{ mt: 1 }} onClick={() => refetch()}>
                  Retry
                </Button>
              </Box>
            ) : filteredCandidates.length > 0 ? (
              <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
                <Table size="sm" stickyHeader>
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>
                        <Checkbox
                          size="sm"
                          checked={allFilteredSelected}
                          indeterminate={someSelected}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Stage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCandidates.map((candidate) => (
                      <tr key={candidate.id}>
                        <td>
                          <Checkbox
                            size="sm"
                            checked={selectedCandidates.includes(candidate.id)}
                            onChange={() => handleToggleCandidate(candidate.id)}
                          />
                        </td>
                        <td>{candidate.name}</td>
                        <td>{candidate.email}</td>
                        <td>
                          <Chip size="sm" variant="soft" color="primary">
                            {candidate.currentStage}
                          </Chip>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Sheet>
            ) : (
              <Typography level="body-sm" color="neutral" sx={{ textAlign: 'center', py: 4 }}>
                {candidates.length === 0 
                  ? (candidatesData?.[pq.candidatesField]?.length > 0 
                    ? 'All candidates have already been imported.' 
                    : 'No candidates found in ATS.') 
                  : 'No candidates match your filters.'}
              </Typography>
            )}
          </Box>

          {/* Import progress */}
          {batchStatus?.status === 'processing' && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography level="body-sm">Importing candidates...</Typography>
                <Typography level="body-sm">
                  {batchStatus.completed + batchStatus.failed} / {batchStatus.total}
                </Typography>
              </Box>
              <LinearProgress 
                determinate 
                value={((batchStatus.completed + batchStatus.failed) / batchStatus.total) * 100} 
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            variant="plain" 
            color="neutral" 
            onClick={handleClose}
            disabled={batchStatus?.status === 'processing'}
          >
            Cancel
          </Button>
          <Button
            variant="solid"
            color="primary"
            loading={importing || batchStatus?.status === 'processing'}
            disabled={selectedCandidates.length === 0 || batchStatus?.status === 'processing'}
            onClick={handleImport}
          >
            {batchStatus?.status === 'processing' 
              ? `Importing (${batchStatus.completed + batchStatus.failed}/${batchStatus.total})` 
              : `Import ${selectedCandidates.length > 0 ? `(${selectedCandidates.length})` : ''}`}
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};

export default ATSCandidateImportDialog;
